import React, { useEffect, useRef, useState, useCallback } from 'react';
import LogoBlanc from '../components/LogoBlanc';
import './ChambreEcoute1.css';

// Composant pour les éléments flottants sonores
const LevitatingElement = ({ 
  element,
  onClick,
  isActive 
}) => {
  const wrapperStyle = {
    filter: isActive 
      ? 'drop-shadow(-3px 6px 10px rgba(40, 212, 198, 0.8))'
      : 'drop-shadow(-3px 6px 3px rgba(50, 50, 0, 0.5))',
    position: 'relative',
    justifyContent: 'center',
    alignItems: 'center',
    transition: 'all 0.4s ease-out',
    margin: '2rem',
    cursor: 'pointer'
  };

  const elementStyle = {
    backgroundImage: `url(${element.backgroundImage})`,
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    width: '400px',
    height: '400px',
    position: 'relative',
    zIndex: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 1,
    animation: 'movement1 8.5s infinite ease-in-out, levitation 6.5s infinite ease-in-out',
    pointerEvents: 'auto',
    cursor: 'pointer',
    clipPath: element.clipPath
  };

  return (
    <div 
      style={wrapperStyle}
      onClick={onClick}
      onMouseEnter={(e) => {
        if (!isActive) {
          e.currentTarget.style.filter = 'drop-shadow(-3px 6px 3px rgba(216, 211, 243, 0.5))';
        }
      }}
      onMouseLeave={(e) => {
        if (!isActive) {
          e.currentTarget.style.filter = 'drop-shadow(-3px 6px 3px rgba(50, 50, 0, 0.5))';
        }
      }}
    >
      <div style={elementStyle} />
    </div>
  );
};

// Composant pour la popup d'autorisation audio
const AudioPopup = ({ isVisible, onClose }) => {
  const closeButtonRef = useRef(null);

  const handleMouseEnter = () => {
    if (closeButtonRef.current) {
      closeButtonRef.current.style.transform = "rotate(0.25turn)";
      closeButtonRef.current.style.color = "rgb(127, 129, 253)";
    }
  };

  const handleMouseLeave = () => {
    if (closeButtonRef.current) {
      closeButtonRef.current.style.transform = "rotate(0turn)";
      closeButtonRef.current.style.color = "white";
    }
  };

  if (!isVisible) return null;

  return (
    <div style={{
      backgroundColor: 'rgb(54, 53, 126)',
      width: '96vw',
      height: '87vh',
      marginTop: '0.5vh',
      position: 'fixed',
      transform: 'translate(-50%, -50%)',
      left: '50%',
      top: '50%',
      borderRadius: '8px',
      fontFamily: '"Roboto", sans-serif',
      zIndex: 999,
      opacity: 0.95,
      transition: 'all 0.5s ease'
    }}>
      <div style={{
        position: 'absolute',
        width: '60%',
        left: '20%',
        top: '40%',
        transform: 'translateY(-50%)',
        padding: '2%',
        backgroundColor: 'transparent',
        color: 'aliceblue',
        fontSize: '2rem',
        letterSpacing: '0.1rem',
        textAlign: 'center'
      }}>
        <h2 style={{ marginBottom: '2rem' }}>Cette page utilise des éléments interactifs</h2>
        <p style={{ 
          fontSize: '1.2rem', 
          marginBottom: '3rem',
          lineHeight: '1.6'
        }}>
          Cliquez sur "Commencer" pour activer l'audio et commencer l'expérience immersive.
          Utilisez la molette de la souris pour naviguer horizontalement.
          <br /><br />
          <strong>Fichiers audio recherchés :</strong>
          <br />• Sons de fond : Trounoir.mp3, Museloop04.wav
          <br />• Éléments : TestVoix_1.wav, TestVoix_2.wav, Humming_1.wav
          <br />
          <small style={{ opacity: 0.8 }}>
            Si vos fichiers ne sont pas trouvés, des oscillateurs de secours seront utilisés.
          </small>
        </p>
        
        <button 
          onClick={onClose}
          style={{
            backgroundColor: 'rgba(40, 212, 198, 0.8)',
            color: 'white',
            border: 'none',
            padding: '15px 30px',
            fontSize: '1.5rem',
            borderRadius: '25px',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            fontFamily: '"Roboto", sans-serif',
            letterSpacing: '0.1rem',
            textTransform: 'uppercase'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = 'rgba(40, 212, 198, 1)';
            e.target.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'rgba(40, 212, 198, 0.8)';
            e.target.style.transform = 'scale(1)';
          }}
        >
          Commencer l'expérience
        </button>
        
        <button 
          ref={closeButtonRef}
          onClick={onClose}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={{
            position: 'absolute',
            top: '20px',
            right: '30px',
            backgroundColor: 'transparent',
            fontSize: '60px',
            border: 'none',
            outline: 'none',
            cursor: 'pointer',
            transform: 'rotate(0turn)',
            color: 'white',
            transition: '0.5s'
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
};

const ChambreEcoute1 = () => {
  // États React
  const [showPopup, setShowPopup] = useState(true);
  const [activeSection, setActiveSection] = useState(0);
  const [audioStatus, setAudioStatus] = useState('non-initialisé');
  const [activeElements, setActiveElements] = useState({
    levitate1: false,
    levitate2: false,
    levitate3: false,
    levitate4: false,
    levitate5: false
  });

  // Refs pour les éléments DOM et audio
  const scrollContainerRef = useRef(null);
  const audioContextRef = useRef(null);
  const sourceARef = useRef(null);
  const sourceBRef = useRef(null);
  const gainNodeARef = useRef(null);
  const gainNodeBRef = useRef(null);
  const levitateSourcesRef = useRef({});
  const levitateGainNodesRef = useRef({});
  const levitatePannerNodesRef = useRef({});
  const progressBarRef = useRef(null);

  // Configuration des éléments flottants avec positions spatiales CORRIGÉES
  const levitatingElements = [
    {
      id: 'levitate1',
      backgroundImage: '/media/Visuels/Watercolors7.jpg',
      clipPath: 'polygon(48% 6%, 35% 3%, 25% 14%, 19% 52%, 20% 83%, 30% 92%, 50% 99%, 64% 99%, 72% 88%, 74% 61%, 70% 30%, 60% 13%)',
      audioFile: '/media/Sons/TestVoix_1.wav',
      spatialPosition: { section: 0, relativeX: 0.3 } // Section 1, côté gauche
    },
    {
      id: 'levitate2',
      backgroundImage: '/media/Visuels/Watercolors3.jpg',
      clipPath: 'polygon(23% 28%, 21% 47%, 30% 78%, 41% 92%, 56% 100%, 72% 90%, 75% 65%, 72% 41%, 62% 24%, 55% 0, 40% 4%, 28% 13%)',
      audioFile: '/media/Sons/TestVoix_2.wav',
      spatialPosition: { section: 0, relativeX: 0.7 } // Section 1, côté droit
    },
    {
      id: 'levitate3',
      backgroundImage: '/media/Visuels/Watercolors12.jpg',
      clipPath: 'polygon(17% 39%, 21% 68%, 26% 91%, 43% 91%, 59% 81%, 68% 73%, 67% 53%, 68% 33%, 63% 20%, 51% 16%, 34% 10%, 23% 12%)',
      audioFile: '/media/Sons/Humming_1.wav',
      spatialPosition: { section: 1, relativeX: 0.2 } // Section 2, côté gauche
    },
    {
      id: 'levitate4',
      backgroundImage: '/media/Visuels/Watercolors9.jpg',
      clipPath: 'polygon(18% 44%, 21% 66%, 30% 88%, 50% 100%, 65% 99%, 73% 75%, 72% 64%, 74% 39%, 65% 19%, 54% 0, 40% 7%, 24% 21%)',
      audioFile: '/media/Sons/Humming_1.wav',
      spatialPosition: { section: 1, relativeX: 0.5 } // Section 2, centre - POSITION CORRIGÉE
    },
    {
      id: 'levitate5',
      backgroundImage: '/media/Visuels/Watercolors14.jpg',
      clipPath: 'polygon(24% 52%, 29% 70%, 38% 86%, 48% 96%, 65% 99%, 75% 85%, 76% 65%, 72% 43%, 65% 29%, 54% 10%, 32% 12%, 25% 33%)',
      audioFile: '/media/Sons/Humming_1.wav',
      spatialPosition: { section: 1, relativeX: 0.8 } // Section 2, côté droit
    }
  ];

  // Fonction pour calculer et appliquer la spatialisation audio
  const updateSpatialAudio = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || !audioContextRef.current) return;

    const scrollLeft = container.scrollLeft;
    const sectionWidth = window.innerWidth;
    
    // Position actuelle du "listener" (utilisateur) en sections continues
    const currentPosition = scrollLeft / sectionWidth;
    
    // Mettre à jour le pan de chaque élément actif
    levitatingElements.forEach(element => {
      const pannerNode = levitatePannerNodesRef.current[element.id];
      const isActive = activeElements[element.id];
      
      if (pannerNode && isActive) {
        // Position absolue de l'élément dans l'espace de la page
        const elementAbsolutePosition = element.spatialPosition.section + element.spatialPosition.relativeX;
        
        // Différence entre la position de l'utilisateur et celle de l'élément
        const relativePosition = elementAbsolutePosition - currentPosition;
        
        // Conversion en valeur de pan (-1 = gauche, 0 = centre, +1 = droite)
        let panValue = Math.max(-1, Math.min(1, relativePosition * 0.8));
        
        // Application fluide du changement de pan
        const now = audioContextRef.current.currentTime;
        pannerNode.pan.cancelScheduledValues(now);
        pannerNode.pan.setValueAtTime(pannerNode.pan.value, now);
        pannerNode.pan.linearRampToValueAtTime(panValue, now + 0.1);
      }
    });
  }, [activeElements, levitatingElements]);

  // Gestionnaire de scroll horizontal avec transitions audio ET spatialisation
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollLeft = container.scrollLeft;
    const maxScroll = container.scrollWidth - container.clientWidth;
    const normalizedScroll = scrollLeft / maxScroll;

    // Calcul de la section active
    const sectionWidth = window.innerWidth;
    const newActiveSection = Math.floor(scrollLeft / sectionWidth);
    setActiveSection(Math.min(3, Math.max(0, newActiveSection)));

    // Mixage audio basé sur le scroll (sons de fond) - seulement si l'audio est initialisé
    if (gainNodeARef.current && gainNodeBRef.current && audioContextRef.current) {
      const mixStart = 0.1;
      const mixEnd = 0.9;
      let mixRatio;

      if (normalizedScroll < mixStart) {
        mixRatio = 0;
      } else if (normalizedScroll > mixEnd) {
        mixRatio = 1;
      } else {
        mixRatio = (normalizedScroll - mixStart) / (mixEnd - mixStart);
      }

      const now = audioContextRef.current.currentTime;

      gainNodeARef.current.gain.cancelScheduledValues(now);
      gainNodeARef.current.gain.setValueAtTime(gainNodeARef.current.gain.value, now);
      gainNodeARef.current.gain.linearRampToValueAtTime(1 - mixRatio, now + 0.1);

      gainNodeBRef.current.gain.cancelScheduledValues(now);
      gainNodeBRef.current.gain.setValueAtTime(gainNodeBRef.current.gain.value, now);
      gainNodeBRef.current.gain.linearRampToValueAtTime(mixRatio, now + 0.1);
    }

    // Mise à jour de la barre de progression
    if (progressBarRef.current && maxScroll > 0) {
      const progress = (scrollLeft / maxScroll) * 25;
      progressBarRef.current.style.width = `${progress}%`;
      progressBarRef.current.style.backgroundColor = `rgba(52, 33, 163, ${0.3 + 0.7 * (progress / 25)})`;
    }

    // Mise à jour de la spatialisation audio
    updateSpatialAudio();
  }, [updateSpatialAudio]);

  // Fonction d'initialisation audio avec vos vrais fichiers
  const initializeAudio = useCallback(async () => {
    try {
      setAudioStatus('initialisation...');
      
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        
        // Création des gains pour le mixage principal
        gainNodeARef.current = audioContextRef.current.createGain();
        gainNodeBRef.current = audioContextRef.current.createGain();

        // Fonction pour charger un fichier audio
        const loadAudioFile = async (url) => {
          try {
            console.log(`Chargement de: ${url}`);
            const response = await fetch(url);
            
            if (!response.ok) {
              throw new Error(`HTTP ${response.status}: Fichier non trouvé - ${url}`);
            }
            
            const arrayBuffer = await response.arrayBuffer();
            const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);
            console.log(`✓ Chargé avec succès: ${url} (durée: ${audioBuffer.duration.toFixed(2)}s)`);
            return audioBuffer;
          } catch (error) {
            console.warn(`✗ Erreur de chargement: ${url} - ${error.message}`);
            return null;
          }
        };

        // Fonction de fallback avec oscillateurs
        const createFallbackOscillators = () => {
          console.log('Création d\'oscillateurs de secours...');
          
          // Oscillateur A (basse fréquence)
          const oscA = audioContextRef.current.createOscillator();
          oscA.type = 'sine';
          oscA.frequency.setValueAtTime(220, audioContextRef.current.currentTime);
          oscA.connect(gainNodeARef.current);
          gainNodeARef.current.connect(audioContextRef.current.destination);
          gainNodeARef.current.gain.setValueAtTime(0.05, audioContextRef.current.currentTime);
          oscA.start();
          
          // Oscillateur B (haute fréquence)
          const oscB = audioContextRef.current.createOscillator();
          oscB.type = 'sine';
          oscB.frequency.setValueAtTime(440, audioContextRef.current.currentTime);
          oscB.connect(gainNodeBRef.current);
          gainNodeBRef.current.connect(audioContextRef.current.destination);
          gainNodeBRef.current.gain.setValueAtTime(0, audioContextRef.current.currentTime);
          oscB.start();
          
          // Oscillateurs pour les éléments flottants
          levitatingElements.forEach((element, index) => {
            const osc = audioContextRef.current.createOscillator();
            const gain = audioContextRef.current.createGain();
            const panner = audioContextRef.current.createStereoPanner();
            
            osc.type = 'triangle';
            osc.frequency.setValueAtTime(330 + (index * 110), audioContextRef.current.currentTime);
            
            osc.connect(gain);
            gain.connect(panner);
            panner.connect(audioContextRef.current.destination);
            
            gain.gain.setValueAtTime(0, audioContextRef.current.currentTime);
            panner.pan.setValueAtTime(0, audioContextRef.current.currentTime);
            
            levitateGainNodesRef.current[element.id] = gain;
            levitatePannerNodesRef.current[element.id] = panner;
            osc.start();
          });
        };

        // Tentative de chargement des sons de fond principaux
        const backgroundSounds = [
          { name: 'SonA', paths: ['/media/Tests/Trounoir.mp3', '/media/Sons/Trounoir.mp3', '/audio/Trounoir.mp3'] },
          { name: 'SonB', paths: ['/media/Sons/Museloop04.wav', '/audio/Museloop04.wav', '/sounds/Museloop04.wav'] }
        ];

        let loadedBackgroundSounds = 0;

        for (const sound of backgroundSounds) {
          let buffer = null;
          
          // Essayer chaque chemin jusqu'à en trouver un qui fonctionne
          for (const path of sound.paths) {
            buffer = await loadAudioFile(path);
            if (buffer) break;
          }

          if (buffer) {
            const source = audioContextRef.current.createBufferSource();
            source.buffer = buffer;
            source.loop = true;

            if (sound.name === 'SonA') {
              source.connect(gainNodeARef.current);
              gainNodeARef.current.connect(audioContextRef.current.destination);
              gainNodeARef.current.gain.setValueAtTime(1, audioContextRef.current.currentTime);
              sourceARef.current = source;
            } else {
              source.connect(gainNodeBRef.current);
              gainNodeBRef.current.connect(audioContextRef.current.destination);
              gainNodeBRef.current.gain.setValueAtTime(0, audioContextRef.current.currentTime);
              sourceBRef.current = source;
            }

            source.start();
            loadedBackgroundSounds++;
            console.log(`✓ ${sound.name} initialisé et démarré`);
          }
        }

        // Chargement des sons des éléments flottants
        let loadedElementSounds = 0;
        
        for (const element of levitatingElements) {
          // Chemins alternatifs pour chaque fichier
          const possiblePaths = [
            element.audioFile,
            element.audioFile.replace('/media/Sons/', '/media/'),
            element.audioFile.replace('/media/Sons/', '/audio/'),
            element.audioFile.replace('/media/Sons/', '/sounds/')
          ];

          let buffer = null;
          for (const path of possiblePaths) {
            buffer = await loadAudioFile(path);
            if (buffer) break;
          }

          if (buffer) {
            const source = audioContextRef.current.createBufferSource();
            const gainNode = audioContextRef.current.createGain();
            const pannerNode = audioContextRef.current.createStereoPanner();

            source.buffer = buffer;
            source.loop = true;
            
            // Chaîne audio: source -> gain -> panner -> destination
            source.connect(gainNode);
            gainNode.connect(pannerNode);
            pannerNode.connect(audioContextRef.current.destination);
            
            gainNode.gain.setValueAtTime(0, audioContextRef.current.currentTime);
            pannerNode.pan.setValueAtTime(0, audioContextRef.current.currentTime);

            levitateSourcesRef.current[element.id] = source;
            levitateGainNodesRef.current[element.id] = gainNode;
            levitatePannerNodesRef.current[element.id] = pannerNode;

            source.start();
            loadedElementSounds++;
            console.log(`✓ Son pour ${element.id} chargé avec spatialisation`);
          } else {
            console.warn(`⚠ Aucun fichier audio trouvé pour ${element.id}`);
          }
        }

        // Utiliser des oscillateurs de secours si aucun fichier n'est chargé
        if (loadedBackgroundSounds === 0) {
          console.log('Aucun son de fond chargé, utilisation des oscillateurs de secours...');
          createFallbackOscillators();
          setAudioStatus('prêt (mode secours - oscillateurs)');
        } else {
          setAudioStatus(`prêt (${loadedBackgroundSounds}/2 sons de fond + ${loadedElementSounds}/${levitatingElements.length} éléments)`);
        }

        // Créer des oscillateurs de secours pour les éléments manquants
        levitatingElements.forEach((element, index) => {
          if (!levitateGainNodesRef.current[element.id]) {
            console.log(`Création d'un oscillateur de secours pour ${element.id}`);
            
            const osc = audioContextRef.current.createOscillator();
            const gain = audioContextRef.current.createGain();
            const panner = audioContextRef.current.createStereoPanner();
            
            osc.type = 'triangle';
            osc.frequency.setValueAtTime(330 + (index * 110), audioContextRef.current.currentTime);
            
            osc.connect(gain);
            gain.connect(panner);
            panner.connect(audioContextRef.current.destination);
            
            gain.gain.setValueAtTime(0, audioContextRef.current.currentTime);
            panner.pan.setValueAtTime(0, audioContextRef.current.currentTime);
            
            levitateGainNodesRef.current[element.id] = gain;
            levitatePannerNodesRef.current[element.id] = panner;
            osc.start();
          }
        });

        console.log('Initialisation audio terminée!');
      }
    } catch (error) {
      console.error('Erreur lors de l\'initialisation audio:', error);
      setAudioStatus('erreur');
    }
  }, [levitatingElements]);

  // Gestionnaire de clic sur les éléments flottants
  const handleElementClick = useCallback((elementId) => {
    const isCurrentlyActive = activeElements[elementId];
    const newState = !isCurrentlyActive;

    setActiveElements(prev => ({
      ...prev,
      [elementId]: newState
    }));

    // Contrôle audio
    const gainNode = levitateGainNodesRef.current[elementId];
    if (gainNode && audioContextRef.current) {
      const newGain = newState ? 0.3 : 0; // Volume plus audible
      const now = audioContextRef.current.currentTime;

      gainNode.gain.cancelScheduledValues(now);
      gainNode.gain.setValueAtTime(gainNode.gain.value, now);
      gainNode.gain.linearRampToValueAtTime(newGain, now + 0.6);
    }

    // Mise à jour de la spatialisation
    setTimeout(() => updateSpatialAudio(), 100);
  }, [activeElements, updateSpatialAudio]);

  // Ref pour éviter les appels multiples pendant l'animation
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef(null);

  // Gestionnaire de scroll wheel instantané avec freinage final
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const container = scrollContainerRef.current;
    if (!container || isScrollingRef.current) return;

    // Micro-débounce pour éviter uniquement les doubles événements
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      isScrollingRef.current = true;
      
      const sectionWidth = window.innerWidth;
      const currentScroll = container.scrollLeft;
      
      // Déterminer la direction du scroll
      const direction = e.deltaY > 0 ? 1 : -1;
      
      // Calculer les ancrages (toutes les demi-sections)
      const anchors = [];
      for (let i = 0; i <= 6; i += 0.5) {
        anchors.push(i * sectionWidth);
      }
      
      // Trouver l'ancrage le plus proche dans la direction souhaitée
      let targetAnchor;
      
      if (direction > 0) {
        targetAnchor = anchors.find(anchor => anchor > currentScroll + 10) || anchors[anchors.length - 1];
      } else {
        const reversedAnchors = [...anchors].reverse();
        targetAnchor = reversedAnchors.find(anchor => anchor < currentScroll - 10) || 0;
      }
      
      // Limiter aux bornes de la page
      const maxScroll = container.scrollWidth - container.clientWidth;
      targetAnchor = Math.max(0, Math.min(maxScroll, targetAnchor));
      
      // Animation avec mouvement instantané + freinage final
      const startTime = performance.now();
      const startScroll = container.scrollLeft;
      const distance = targetAnchor - startScroll;
      const duration = 140; // Durée courte pour le freinage seulement
      
      const animateScroll = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Courbe avec mouvement instantané + freinage final satisfaisant
        let easeProgress;
        if (progress < 0.7) {
          // 70% du mouvement: instantané (linéaire rapide)
          easeProgress = progress / 0.7;
        } else {
          // 30% final: freinage doux et satisfaisant (ease-out cubique)
          const finalProgress = (progress - 0.7) / 0.3;
          easeProgress = 1 - Math.pow(1 - finalProgress, 3) * 0.1; // Petite courbe de freinage
        }
        
        container.scrollLeft = startScroll + (distance * easeProgress);
        
        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        } else {
          isScrollingRef.current = false;
        }
      };
      
      requestAnimationFrame(animateScroll);
    }, 3); // Délai minimal
  }, []);

  // Navigation par points
  const navigateToSection = useCallback((index) => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollLeft = index * window.innerWidth;
    }
  }, []);

  // Fermeture de la popup et initialisation audio
  const handleClosePopup = useCallback(async () => {
    setShowPopup(false);
    await initializeAudio();
  }, [initializeAudio]);

  // Effects
  useEffect(() => {
    // Afficher la popup au chargement
    const timer = setTimeout(() => {
      setShowPopup(true);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.addEventListener('wheel', handleWheel, { passive: false });
    container.addEventListener('scroll', handleScroll);

    return () => {
      container.removeEventListener('wheel', handleWheel);
      container.removeEventListener('scroll', handleScroll);
    };
  }, [handleWheel, handleScroll]);

  // Cleanup amélioré
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
    };
  }, []);

  return (
    <div style={{
      width: '100%',
      height: '100vh',
      overflow: 'hidden',
      position: 'relative',
      fontFamily: '"Roboto", sans-serif',
      backgroundColor: '#0e0929'
    }}>
      {/* Styles CSS intégrés */}
      <style>{`
        @keyframes levitation {
          0% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
          100% { transform: translateY(0); }
        }
        
        @keyframes movement1 {
          0% { transform: translateY(0); }
          25% { transform: translateX(-5px) translateY(-10px); }
          50% { transform: translateX(4px) translateY(-6px); }
          100% { transform: translateY(0); }
        }
        
        /* Masquer la scrollbar */
        .horizontal-scroll::-webkit-scrollbar {
          display: none;
        }
        
        .horizontal-scroll {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        
        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>

      <LogoBlanc />
      <h1 style={{
        position: 'fixed',
        top: '2.7rem',
        right: '60px',
        fontFamily: "'Source Sans 3', sans-serif",
        fontSize: 'clamp(2.5rem, 4vw, 3rem)',
        fontWeight: 300,
        color: 'rgba(255,255,255,0.55)',
        letterSpacing: '0.2rem',
        textAlign: 'right',
        textTransform: 'uppercase',
        margin: 0,
        pointerEvents: 'none',
        zIndex: 1000,
        lineHeight: 1,
      }}>Chambre d'Écoute #1</h1>

      {/* Indicateur d'état audio */}
      <div style={{
        position: 'fixed',
        top: '10px',
        left: '200px',
        background: 'rgba(0,0,0,0.7)',
        color: 'white',
        padding: '10px',
        borderRadius: '5px',
        fontSize: '12px',
        zIndex: 1000
      }}>
        Audio: {audioStatus}
        {audioStatus.includes('prêt') && (
          <div style={{ marginTop: '5px', fontSize: '10px' }}>
            Cliquez sur les éléments flottants • Naviguez horizontalement
          </div>
        )}
      </div>

      {/* Popup d'autorisation audio */}
      <AudioPopup isVisible={showPopup} onClose={handleClosePopup} />

      {/* Barre de progression */}
      <div ref={progressBarRef} style={{
        position: 'fixed',
        bottom: '2.27%',
        left: '37.5%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '5px',
        width: '0',
        backgroundColor: 'rgba(52, 33, 163, 0.3)',
        borderRadius: '10px',
        zIndex: 1,
        transition: 'background-color 0.3s ease'
      }}>
        {/* Points de navigation */}
        <div style={{
          position: 'fixed',
          alignItems: 'center',
          bottom: '2%',
          left: '37.5%',
          display: 'flex',
          justifyContent: 'space-between',
          width: '25%',
          zIndex: 2
        }}>
          {[0, 1, 2, 3].map(index => (
            <div
              key={index}
              style={{
                clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
                width: '10px',
                height: '10px',
                backgroundColor: 'rgb(52, 33, 163)',
                opacity: activeSection === index ? 1 : 0.2,
                cursor: 'pointer',
                transition: 'all 0.4s ease-out',
                transform: activeSection === index ? 'scale(1.8)' : 'scale(1)',
                willChange: 'transform'
              }}
              onClick={() => navigateToSection(index)}
              onMouseEnter={(e) => {
                if (activeSection !== index) {
                  e.target.style.backgroundColor = 'rgb(197, 188, 247)';
                  e.target.style.opacity = '1';
                }
              }}
              onMouseLeave={(e) => {
                if (activeSection !== index) {
                  e.target.style.backgroundColor = 'rgb(52, 33, 163)';
                  e.target.style.opacity = '0.2';
                }
              }}
            />
          ))}
        </div>
      </div>

      {/* Container de scroll horizontal */}
      <div 
        ref={scrollContainerRef} 
        className="horizontal-scroll"
        style={{
          display: 'flex',
          overflowX: 'auto',
          overflowY: 'hidden',
          scrollBehavior: 'smooth',
          height: '100vh',
          msOverflowStyle: 'none',
          scrollbarWidth: 'none'
        }}
        
      >
        {/* Section 1 - Éléments 1 et 2 */}
        <div style={{
          minWidth: '100vw',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-around',
          fontSize: '2rem',
          position: 'relative',
          backgroundImage: 'linear-gradient(to right, rgb(47, 21, 82), rgb(76, 129, 131))',
          padding: '0 5rem'
        }}>
          {levitatingElements.slice(0, 2).map(element => (
            <LevitatingElement
              key={element.id}
              element={element}
              onClick={() => handleElementClick(element.id)}
              isActive={activeElements[element.id]}
            />
          ))}
        </div>

        {/* Section 2 - Éléments 3, 4 et 5 */}
        <div style={{
          minWidth: '100vw',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-around',
          fontSize: '2rem',
          position: 'relative',
          backgroundImage: 'linear-gradient(to right, rgb(76, 129, 131), rgb(30, 114, 96))',
          flexWrap: 'wrap',
          padding: '2rem'
        }}>
          {levitatingElements.slice(2).map(element => (
            <LevitatingElement
              key={element.id}
              element={element}
              onClick={() => handleElementClick(element.id)}
              isActive={activeElements[element.id]}
            />
          ))}
        </div>

        {/* Section 3 */}
        <div style={{
          minWidth: '100vw',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '2rem',
          position: 'relative',
          backgroundImage: 'linear-gradient(to right, rgb(30, 114, 96), rgb(123, 124, 204))'
        }}>
          <h2 style={{color: 'white', fontSize: '2rem', textAlign: 'center', opacity: 0.7}}>
            Section 3<br />
            <span style={{fontSize: '1.5rem'}}>Espace pour futures expérimentations</span>
          </h2>
        </div>

        {/* Section 4 */}
        <div style={{
          minWidth: '100vw',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '2rem',
          position: 'relative',
          backgroundImage: 'linear-gradient(to right, rgb(123, 124, 204), rgb(47, 21, 82))'
        }}>
          <h2 style={{color: 'white', fontSize: '2rem', textAlign: 'center', opacity: 0.7}}>
            Section 4<br />
            <span style={{fontSize: '1.5rem'}}>Espace pour futures expérimentations</span>
          </h2>
        </div>
      </div>
    </div>
  );
};

export default ChambreEcoute1;